(() => {
  const SELECTOR = '[data-product-lightbox]';

  function initializeLightbox(dialog) {
    if (!dialog || dialog.dataset.initialized === 'true' || typeof dialog.showModal !== 'function') return;
    dialog.dataset.initialized = 'true';

    const root = dialog.closest('.custom-product-section')?.parentElement || document;
    const mediaContainer = root.querySelector('#main-media-container');
    const surface = dialog.querySelector('[data-lightbox-surface]');
    const image = dialog.querySelector('[data-lightbox-image]');
    const sources = Array.from(dialog.querySelectorAll('[data-lightbox-source]'));
    const closeButton = dialog.querySelector('[data-lightbox-close]');
    const zoomInButton = dialog.querySelector('[data-lightbox-zoom-in]');
    const zoomOutButton = dialog.querySelector('[data-lightbox-zoom-out]');
    const zoomResetButton = dialog.querySelector('[data-lightbox-zoom-reset]');
    const thumbnails = dialog.querySelector('[data-lightbox-thumbnails]');
    const counter = dialog.querySelector('[data-lightbox-counter]');
    const speed = Math.max(100, Number(dialog.dataset.speed) || 320);
    const baseZoom = Math.max(1, Math.min(1.5, (Number(dialog.dataset.zoomLevel) || 100) / 100));
    const minZoom = 1;
    const maxZoom = 3;
    const zoomStep = 0.25;
    const canSwipe = dialog.dataset.enableSwipe === 'true';
    const canUseKeyboard = dialog.dataset.enableKeyboard === 'true';
    const highResImagesLoaded = new Set();
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let currentIndex = 0;
    let zoomLevel = baseZoom;
    let returnFocus;
    let touchStartX = 0;
    let pinchStartDistance = 0;
    let panX = 0;
    let panY = 0;
    let dragStartX = 0;
    let dragStartY = 0;
    let dragOriginX = 0;
    let dragOriginY = 0;
    let touchDragStartX = 0;
    let touchDragStartY = 0;
    let touchDragOriginX = 0;
    let touchDragOriginY = 0;
    let isDragging = false;

    dialog.style.setProperty('--lightbox-duration', `${speed}ms`);
    dialog.style.setProperty('--lightbox-overlay', `rgba(255, 255, 255, ${dialog.dataset.opacity || '0.98'})`);

    const preloadImage = (source) => {
      const highResolutionUrl = source?.dataset.fullSrc;
      if (!highResolutionUrl || highResImagesLoaded.has(highResolutionUrl)) return;

      const preloadedImage = new Image();
      preloadedImage.decoding = 'async';
      preloadedImage.onload = () => highResImagesLoaded.add(highResolutionUrl);
      preloadedImage.src = highResolutionUrl;
    };

    const scrollThumbnailIntoView = (source) => {
      source?.scrollIntoView({
        behavior: reducedMotion.matches ? 'instant' : 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    };

    const updatePan = (nextX, nextY) => {
      panX = zoomLevel <= minZoom ? 0 : nextX;
      panY = zoomLevel <= minZoom ? 0 : nextY;
      image.style.setProperty('--lightbox-pan-x', `${panX}px`);
      image.style.setProperty('--lightbox-pan-y', `${panY}px`);
    };

    const updateZoom = (nextZoom) => {
      zoomLevel = Math.max(minZoom, Math.min(maxZoom, nextZoom));
      image.style.setProperty('--lightbox-scale', String(zoomLevel));
      if (zoomLevel <= minZoom) updatePan(0, 0);
      image.classList.toggle('is-draggable', zoomLevel > minZoom);
      zoomOutButton.disabled = zoomLevel <= minZoom;
      zoomInButton.disabled = zoomLevel >= maxZoom;
      zoomResetButton.textContent = `${Math.round(zoomLevel * 100)}%`;
      zoomResetButton.setAttribute('aria-label', `Reset image zoom to ${Math.round(zoomLevel * 100)} percent`);
    };

    const changeZoom = (amount) => updateZoom(zoomLevel + amount);

    const setTriggerState = () => {
      const currentImage = mediaContainer?.querySelector('#main-image');
      if (currentImage && dialog.dataset.enableLightbox === 'true') {
        currentImage.classList.add('product-lightbox__trigger');
        currentImage.setAttribute('tabindex', '0');
        currentImage.setAttribute('role', 'button');
        currentImage.setAttribute('aria-label', 'Open product image gallery');
      }
    };

    const updateControls = () => {
      thumbnails.hidden = dialog.dataset.enableThumbnails !== 'true' || sources.length < 2;
      counter.hidden = sources.length < 2;
      if (!counter.hidden) counter.textContent = `${currentIndex + 1} / ${sources.length}`;
      sources.forEach((source, index) => {
        const isActive = index === currentIndex;
        source.setAttribute('aria-current', String(isActive));
        source.setAttribute('aria-selected', String(isActive));
      });
    };

    const render = (index) => {
      if (!sources.length) return;
      currentIndex = (index + sources.length) % sources.length;
      const source = sources[currentIndex];
      image.classList.remove('is-loaded');
      image.onload = () => image.classList.add('is-loaded');
      image.src = source.dataset.fullSrc;
      image.alt = source.dataset.alt || '';
      if (image.complete) image.classList.add('is-loaded');
      updatePan(0, 0);
      updateZoom(baseZoom);
      updateControls();
      scrollThumbnailIntoView(source);
      preloadImage(sources[currentIndex + 1]);
      preloadImage(sources[currentIndex - 1]);
    };

    const close = () => {
      if (!dialog.open || dialog.classList.contains('is-closing')) return;
      dialog.classList.add('is-closing');
      window.setTimeout(() => {
        dialog.classList.remove('is-closing');
        dialog.close();
      }, speed);
    };

    const open = (trigger) => {
      const mediaId = trigger?.dataset.mediaId;
      const sourceIndex = sources.findIndex((source) => source.dataset.mediaId === mediaId);
      if (sourceIndex < 0 && !sources.length) return;
      returnFocus = trigger || document.activeElement;
      render(sourceIndex >= 0 ? sourceIndex : 0);
      dialog.showModal();
      closeButton.focus();
      document.documentElement.classList.add('product-lightbox-open');
      document.body.classList.add('product-lightbox-open');
    };

    mediaContainer?.addEventListener('click', (event) => {
      const trigger = event.target.closest('#main-image');
      if (trigger && mediaContainer.contains(trigger)) open(trigger);
    });

    mediaContainer?.addEventListener('keydown', (event) => {
      const trigger = event.target.closest('#main-image');
      if (trigger && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        open(trigger);
      }
    });

    closeButton.addEventListener('click', close);
    zoomInButton.addEventListener('click', () => changeZoom(zoomStep));
    zoomOutButton.addEventListener('click', () => changeZoom(-zoomStep));
    zoomResetButton.addEventListener('click', () => updateZoom(baseZoom));
    sources.forEach((source, index) => {
      source.addEventListener('click', () => render(index));
      source.addEventListener('pointerenter', () => preloadImage(source));
      source.addEventListener('focus', () => preloadImage(source));
    });

    image.addEventListener('wheel', (event) => {
      event.preventDefault();
      changeZoom(event.deltaY < 0 ? zoomStep : -zoomStep);
    }, { passive: false });

    image.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch' || zoomLevel <= minZoom) return;
      isDragging = true;
      dragStartX = event.clientX;
      dragStartY = event.clientY;
      dragOriginX = panX;
      dragOriginY = panY;
      image.classList.add('is-dragging');
      image.setPointerCapture(event.pointerId);
    });

    image.addEventListener('pointermove', (event) => {
      if (!isDragging) return;
      updatePan(
        dragOriginX + event.clientX - dragStartX,
        dragOriginY + event.clientY - dragStartY
      );
    });

    const stopDragging = (event) => {
      if (!isDragging) return;
      isDragging = false;
      image.classList.remove('is-dragging');
      if (event?.pointerId !== undefined && image.hasPointerCapture(event.pointerId)) {
        image.releasePointerCapture(event.pointerId);
      }
    };

    image.addEventListener('pointerup', stopDragging);
    image.addEventListener('pointercancel', stopDragging);
    image.addEventListener('lostpointercapture', stopDragging);

    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      close();
    });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog || event.target === surface) close();
    });

    dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('product-lightbox-open');
      document.body.classList.remove('product-lightbox-open');
      if (returnFocus && document.contains(returnFocus)) returnFocus.focus();
      returnFocus = null;
    });

    if (canUseKeyboard) {
      dialog.addEventListener('keydown', (event) => {
        if (event.key === '+' || event.key === '=') {
          event.preventDefault();
          changeZoom(zoomStep);
        } else if (event.key === '-' || event.key === '_') {
          event.preventDefault();
          changeZoom(-zoomStep);
        } else if (event.key === '0') {
          event.preventDefault();
          updateZoom(baseZoom);
        }
      });
    }

    image.addEventListener('touchstart', (event) => {
      if (event.touches.length === 1 && zoomLevel > minZoom) {
        isDragging = true;
        touchDragStartX = event.touches[0].clientX;
        touchDragStartY = event.touches[0].clientY;
        touchDragOriginX = panX;
        touchDragOriginY = panY;
        image.classList.add('is-dragging');
      }
      if (event.touches.length === 2) {
        isDragging = false;
        pinchStartDistance = Math.hypot(
          event.touches[0].clientX - event.touches[1].clientX,
          event.touches[0].clientY - event.touches[1].clientY
        );
      }
    }, { passive: true });

    image.addEventListener('touchmove', (event) => {
      if (event.touches.length === 1 && isDragging && zoomLevel > minZoom) {
        event.preventDefault();
        updatePan(
          touchDragOriginX + event.touches[0].clientX - touchDragStartX,
          touchDragOriginY + event.touches[0].clientY - touchDragStartY
        );
        return;
      }
      if (event.touches.length !== 2 || !pinchStartDistance) return;
      event.preventDefault();
      const distance = Math.hypot(
        event.touches[0].clientX - event.touches[1].clientX,
        event.touches[0].clientY - event.touches[1].clientY
      );
      updateZoom(zoomLevel * (distance / pinchStartDistance));
      pinchStartDistance = distance;
    }, { passive: false });

    image.addEventListener('touchend', () => {
      pinchStartDistance = 0;
      isDragging = false;
      image.classList.remove('is-dragging');
    }, { passive: true });

    if (canSwipe) {
      surface.addEventListener('touchstart', (event) => {
        touchStartX = event.changedTouches[0].clientX;
      }, { passive: true });
      surface.addEventListener('touchend', (event) => {
        const distance = event.changedTouches[0].clientX - touchStartX;
        if (zoomLevel <= minZoom && Math.abs(distance) > 48) render(currentIndex + (distance < 0 ? 1 : -1));
      }, { passive: true });
    }

    const observer = new MutationObserver(setTriggerState);
    if (mediaContainer) observer.observe(mediaContainer, { childList: true, subtree: true });
    setTriggerState();
    updateControls();
  }

  const initializeAll = (scope = document) => scope.querySelectorAll(SELECTOR).forEach(initializeLightbox);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeAll());
  else initializeAll();
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
